import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PasswordStrengthBar from 'react-password-strength-bar';
import InputField from '../InputField';
import Button from '../Button';
import Spinner from '../Spinner';
import Alert from '../Alert';
import { useAuth } from '../Authentication/AuthProvider';
import { adminUpdateAnotherUser, setUserConfirmed, resendUserConfirmationEmail } from '../../api/usersAPI';
import { deleteToken } from '../../api/authenticationAPI';
import { COLORS } from '../../utils/constants/colors';
import Select from '../Select';

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: row;
`;

const Form = styled.form`
  padding: 0 0.5rem 0 0;
`;

const ButtonGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 1rem;
  padding-top: 1rem;
  border-top: solid 1px ${COLORS.lighterGray};
`;

const ButtonCentered = styled(Button)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: fit-content !important;
`;

const StyledPasswordStrengthBar = styled(PasswordStrengthBar)`
  width: 13rem;
  margin-left: 0.5rem;
`;

const PasswordStrengthWrapper = styled.div`
  width: 13rem;
  margin-left: 0.5rem;
`;

const CenteredField = styled.div`
  width: 12.7rem;
`;

const UserDetails = ({
  data,
  isUserLoggedInAdmninistrator,
  setWasUserChanged,
}) => {
  const isUserAdministrator = data?.role === 'administrator';
  const [userDetails, setUserDetails] = useState({});
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordHasChanged, setPasswordHasChanged] = useState(false);
  const [userHasBeenChanged, setUserHasBeenChanged] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageSecondLine, setMessageSecondLine] = useState('');
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [localConfirmed, setLocalConfirmed] = useState(!!data?.confirmed);
  const initialErrors = {
    token: '',
    currentPassword: '',
    password: '',
    passwordConfirmation: '',
  };
  const [error, setError] = useState(initialErrors);

  const { token, userID } = useAuth();
  const navigate = useNavigate();

  const { username, role, email, firstName, surname } = userDetails;

  const isEditingSelf =
    isUserLoggedInAdmninistrator ?? Number(data?.id) === Number(userID);
  const emailChanged = email !== undefined && email !== data?.email;
  const needsCurrentPassword =
    isEditingSelf && (passwordHasChanged || emailChanged);
  const showPasswordFields = !isUserAdministrator || isEditingSelf;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserDetails({ ...userDetails, [name]: value });
  };

  const handleEditUser = (e) => {
    e.preventDefault();
    setMessage('');
    setIsEditingUser(true);
    setIsFormValid(true);
  };

  const handleSelectChange = (e) => {
    setUserDetails({ ...userDetails, role: e.target.value });
  };

  const handleSubmitChanges = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const response = await adminUpdateAnotherUser(
      token,
      userDetails,
      passwordHasChanged,
      password,
      currentPassword,
      isEditingSelf,
      data
    );

    if (response.status === 200) {
      setMessage('User updated successfully.');
      if (isEditingSelf && passwordHasChanged) {
        setMessageSecondLine('Redirecting to login page...');
      }
      setUserHasBeenChanged(true);
      setPassword('');
      setPasswordConfirmation('');
      setCurrentPassword('');
      setError(initialErrors);
      setIsEditingUser(false);
      setIsFormValid(false);
      if (!isEditingSelf) {
        setWasUserChanged(true);
      }
      if (isEditingSelf && passwordHasChanged && response.status === 200) {
        setTimeout(() => {
          deleteToken(token);
          navigate('/login');
        }, 5000);
      }
    } else {
      setUserHasBeenChanged(false);
      setError(initialErrors);
      const validationDetail = response?.data?.errors?.[0]?.detail;
      const validationCode = response?.data?.errors?.[0]?.code;
      setMessage(
        validationCode === 'username in use'
          ? 'This username is already taken by another user.'
          : validationDetail || validationCode || 'Failed to update user.',
      );
      setCurrentPassword('');
      if (!isEditingSelf) {
        setWasUserChanged(false);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const passwordsMatch = password === passwordConfirmation;
    const passwordOk = !passwordHasChanged || (passwordsMatch && passwordStrength);
    const currentPasswordOk =
      !needsCurrentPassword || currentPassword.length > 0;
    setIsFormValid(passwordOk && currentPasswordOk);
  }, [
    password,
    passwordConfirmation,
    currentPassword,
    passwordHasChanged,
    passwordStrength,
    needsCurrentPassword,
  ]);

  useEffect(() => {
    if (password.length) {
      setPasswordHasChanged(true);
      setMessage('');
    } else {
      setPasswordHasChanged(false);
    }
  }, [password]);

  useEffect(() => {
    setUserDetails(data);
  }, [data]);

  useEffect(() => {
    setLocalConfirmed(!!data?.confirmed);
  }, [data?.confirmed]);

  const handleToggleConfirmed = async () => {
    setIsConfirmLoading(true);
    const newConfirmed = !localConfirmed;
    const response = await setUserConfirmed(token, data?.id, newConfirmed);
    if (response?.status === 200) {
      setLocalConfirmed(newConfirmed);
      setWasUserChanged(true);
    }
    setIsConfirmLoading(false);
  };

  const handleResendConfirmation = async () => {
    setIsConfirmLoading(true);
    setResendMessage('');
    const response = await resendUserConfirmationEmail(token, data?.id);
    if (response?.status === 200) {
      setResendMessage('Confirmation email sent successfully.');
    } else {
      setResendMessage('Failed to send confirmation email.');
    }
    setIsConfirmLoading(false);
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    if (name === 'new-password') {
      if (!password) {
        setError({ ...error, password: 'Password is required' });
      } else if (passwordStrength < 1) {
        setError({ ...error, password: 'Please enter a strong password' });
      } else {
        setError({ ...error, password: '' });
      }
    }
    if (name === 'new-password-confirm') {
      if (!passwordConfirmation) {
        setError({
          ...error,
          passwordConfirmation: 'Password is required',
        });
      } else if (password !== passwordConfirmation) {
        setError({ ...error, passwordConfirmation: 'Passwords do not match' });
        setPasswordConfirmation('');
      } else {
        setError({ ...error, passwordConfirmation: '' });
      }
    }
    if (name === 'current-password') {
      if (!currentPassword) {
        setError({ ...error, currentPassword: 'Password is required' });
      } else {
        setError({ ...error, currentPassword: '' });
      }
    }
  };

  return (
    <Form onSubmit={isEditingUser ? handleSubmitChanges : handleEditUser}>
      <Rows>
        <Grid>
          <InputField
            label="Username"
            name="username"
            type="text"
            value={username || ''}
            onChange={handleInputChange}
            disabled={!isEditingUser}
          />
          <Select
            label="Role"
            onSelectChange={handleSelectChange}
            userDetails={userDetails}
            isEditingUser={
              isEditingUser && !isEditingSelf && !isUserAdministrator
            }
            value={role}
          />
        </Grid>
        <InputField
          label="Email"
          name="email"
          type="email"
          value={email || ''}
          onChange={handleInputChange}
          disabled={!isEditingUser}
        />
        <Grid>
          <InputField
            label="First Name"
            name="firstName"
            type="text"
            value={firstName || ''}
            onChange={handleInputChange}
            disabled={!isEditingUser}
          />
          <InputField
            label="Surname"
            name="surname"
            type="text"
            value={surname || ''}
            onChange={handleInputChange}
            disabled={!isEditingUser}
          />
        </Grid>

        {showPasswordFields && (
          <>
            <Grid>
              <InputField
                label="New password"
                name="new-password"
                placeholder="New Password"
                type="password"
                value={password}
                onChange={(e) => {
                  setError({ ...error, password: '' });
                  setPassword(e.target.value);
                }}
                onBlur={handleBlur}
                error={error.password}
                disabled={!isEditingUser}
              />

              <InputField
                label="Confirm password"
                name="new-password-confirm"
                placeholder="New Password confirm"
                type="password"
                value={passwordConfirmation}
                onChange={(e) => {
                  setError({ ...error, passwordConfirmation: '' });
                  setPasswordConfirmation(e.target.value);
                }}
                onBlur={handleBlur}
                error={error.passwordConfirmation}
                disabled={!isEditingUser}
              />
            </Grid>

            {isEditingUser && (
              <PasswordStrengthWrapper>
                <StyledPasswordStrengthBar
                  password={password}
                  barColors={[
                    COLORS.gray,
                    COLORS.red,
                    COLORS.orange,
                    COLORS.green,
                    COLORS.green,
                    COLORS.green,
                  ]}
                  onChangeScore={(score) => {
                    setPasswordStrength(score > 1);
                  }}
                />
              </PasswordStrengthWrapper>
            )}
          </>
        )}

        {needsCurrentPassword ? (
          <CenteredField>
            <InputField
              label="Current password"
              name="current-password"
              placeholder="Current password"
              type="password"
              value={currentPassword}
              onChange={(e) => {
                setError({ ...error, currentPassword: '' });
                setCurrentPassword(e.target.value);
              }}
              onBlur={handleBlur}
              error={error.currentPassword}
              disabled={!isEditingUser}
            />
          </CenteredField>
        ) : null}

        {userHasBeenChanged ? (
          <>
            <Alert success>{message}</Alert>
            <Alert success>{messageSecondLine}</Alert>
          </>
        ) : (
          <Alert error>{message}</Alert>
        )}
        {!isEditingSelf && (
          <ButtonGroup>
            <ButtonCentered
              type="button"
              onClick={handleToggleConfirmed}
              disabled={isConfirmLoading}
            >
              {localConfirmed ? 'Mark as Unconfirmed' : 'Mark as Confirmed'}
            </ButtonCentered>
            {!localConfirmed && (
              <ButtonCentered
                type="button"
                onClick={handleResendConfirmation}
                disabled={isConfirmLoading}
              >
                Resend confirmation email
              </ButtonCentered>
            )}
          </ButtonGroup>
        )}
        {resendMessage && (
          <Alert
            success={resendMessage.includes('success')}
            error={!resendMessage.includes('success')}
          >
            {resendMessage}
          </Alert>
        )}
        <ButtonGroup>
          {isEditingUser ? (
            <ButtonCentered type="submit" disabled={!isFormValid}>
              Save Changes
            </ButtonCentered>
          ) : (
            <ButtonCentered type="submit">Edit User</ButtonCentered>
          )}
        </ButtonGroup>
      </Rows>
      {isLoading && <Spinner />}
    </Form>
  );
};

export default UserDetails;
